/**
 * Working data that belongs to whoever was signed in.
 *
 * None of it is namespaced by account, so it has to be dropped when the session
 * changes: the conversation someone set up against one account describes a
 * customer the next account has never heard of.
 */

/**
 * All the simulator keeps: the conversation someone set up and the world it runs
 * against, each tab its own. Everything else about a run is held in memory and
 * goes with the page. Only this tab's copy can be reached from here; the others
 * belong to sessions that are still theirs to finish, and each clears its own the
 * moment its account changes.
 */
export const TAB_SCOPED_KEYS = ['chat-messages-kim-ai', 'sim-profile-kim-ai'];

/** Shared by every tab on the origin, so clearing them in one tab clears them for all. */
export const ACCOUNT_SCOPED_KEYS = [
	'back-testing-typemap-meta-kim-ai',
	// The selected business unit. Account-scoped for the same reason as the rest: a brand
	// belongs to one account, so carrying it into the next one would silently scope every
	// screen by an id that account does not own.
	'selected-business-unit-kim-ai',
	// The Playground's last-used channel: a channel belongs to one account's brand.
	'playground-channel-kim-ai'
];

/**
 * Kept by no version of this app any more: the hand-off is named by the URL it
 * came from, the conversation id is a nonce, and tool simulation starts on for
 * every fresh page. Dropped from both stores, since they have been in each.
 */
const UNKEPT_KEYS = ['sim-session-kim-ai', 'sim-case-attempts-kim-ai', 'simulate-tools-kim-ai'];

/**
 * Written to the shared store by earlier versions and read by none of this one:
 * what the simulator still keeps has moved to sessionStorage, and the scenario
 * types became a query with no store of their own.
 */
export const RETIRED_KEYS = [
	...TAB_SCOPED_KEYS,
	...UNKEPT_KEYS,
	'back-testing-scenario-types-kim-ai',
	'back-testing-scenarios-kim-ai',
	'back-testing-cases-kim-ai',
	'back-testing-sop-text-kim-ai',
	'sim-datasets-kim-ai'
];

/** Shed on the way in, so a browser that has been running this app stops carrying them. */
export const dropRetiredStorage = (): void => {
	for (const key of RETIRED_KEYS) localStorage.removeItem(key);
	for (const key of UNKEPT_KEYS) sessionStorage.removeItem(key);
};

export const clearAccountScopedStorage = (): void => {
	for (const key of ACCOUNT_SCOPED_KEYS) {
		localStorage.removeItem(key);
		// removeItem only notifies other documents, so without this the atoms in this
		// one keep serving the previous account's values until a reload.
		if (typeof window !== 'undefined') {
			window.dispatchEvent(new StorageEvent('storage', { key, storageArea: localStorage, newValue: null }));
		}
	}
	for (const key of TAB_SCOPED_KEYS) {
		sessionStorage.removeItem(key);
		if (typeof window !== 'undefined') {
			window.dispatchEvent(new StorageEvent('storage', { key, storageArea: sessionStorage, newValue: null }));
		}
	}
};
